/**
 * executionStatusHelpers - Build ExecutionStatus from streaming events.
 *
 * Normalizes raw WebSocket events into the unified ExecutionStatus model.
 * Handles incremental updates, step creation, status transitions, and phase derivation.
 */

import type {
  ContextRetrievalStep,
  ExecutionStatus,
  ExecutionStep,
  GenericProgressStep,
  PlanStep,
  ResponseGenerationStep,
  ServerEvent,
  ToolExecutionStep,
} from '@protoss/shared';
import { deriveExecutionPhase } from '@protoss/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Legacy chat child entry types (kept here so other features can reuse them)
// ─────────────────────────────────────────────────────────────────────────────

export interface StatusEntry {
  kind: 'status';
  id: string;
  label: string;
  ts: string;
}

export interface ToolEntry {
  kind: 'tool';
  toolCallId: string;
  toolName: string;
  label?: string;
  status: 'running' | 'completed' | 'failed';
  resultSummary?: string;
  /** Accumulated delta text shown while the tool is running */
  progressText?: string;
  ts: string;
}

export type RunChildEntry = StatusEntry | ToolEntry;

// ─────────────────────────────────────────────────────────────────────────────
// Initialize execution status
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create initial execution status for a new run.
 */
export function createInitialExecutionStatus(startedAt: string): ExecutionStatus {
  return {
    phase: 'queued',
    steps: [],
    startedAt,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event → Step mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a generic progress step from a 'status' event.
 */
export function createProgressStep(event: ServerEvent & { type: 'status' }): GenericProgressStep {
  return {
    id: `step-status-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type: 'generic_progress',
    status: 'completed',
    label: event.label,
    order: event.seq,
    startedAt: event.ts,
    completedAt: event.ts,
  };
}

/**
 * Create a context retrieval step when RAG references are emitted.
 */
export function createContextRetrievalStep(
  event: ServerEvent & { type: 'rag_references' }
): ContextRetrievalStep {
  return {
    id: `step-context-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type: 'retrieve_context',
    status: 'completed',
    label: 'Retrieved context',
    sourceCount: event.references.length,
    citedCount: event.references.filter((r) => r.cited).length,
    order: event.seq,
    startedAt: event.ts,
    completedAt: event.ts,
  };
}

/**
 * Create a tool execution step from a 'tool_started' event.
 */
export function createToolExecutionStep(
  event: ServerEvent & { type: 'tool_started' }
): ToolExecutionStep {
  return {
    id: `step-tool-${event.toolCallId}`,
    type: 'tool_execution',
    status: 'running',
    label: event.label ?? event.toolName,
    toolCallId: event.toolCallId,
    toolName: event.toolName,
    order: event.seq,
    startedAt: event.ts,
  };
}

/**
 * Create a response generation step when message streaming begins.
 */
export function createResponseGenerationStep(
  event: ServerEvent & { type: 'message_started' }
): ResponseGenerationStep {
  return {
    id: `step-response-${event.messageId}`,
    type: 'generate_response',
    status: 'running',
    label: 'Generating response',
    order: event.seq,
    startedAt: event.ts,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent step lifecycle helpers (agent_step_started / agent_step_completed / failed)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create pending PlanStep items from a `plan_created` event.
 * These are prepended to executionStatus.steps so the Todo drawer can render
 * the plan before skill execution begins.
 */
/**
 * Sort key for a plan step that has NOT started yet. All plan steps are created from a single
 * `plan_created` event near the start of the run, so ordering them by that event's seq would bolt
 * the whole plan block ABOVE the narration that streams later (seq > plan_created). Instead a
 * pending step sorts to the BOTTOM of the chain (a queued "upcoming" preview); when it starts,
 * `plan_step_started` restamps its `order` to that event's seq so it moves INLINE to its execution
 * point and interleaves with the phase narration (Claude-style: step -> its narration -> next step).
 * Kept below ChatRunView's SEGMENT_FALLBACK_BASE (MAX/2) so it never outranks a fallback segment.
 */
export const PENDING_PLAN_STEP_ORDER_BASE = Number.MAX_SAFE_INTEGER / 4;

export function createPlanStepsFromExecutionPlan(
  event: ServerEvent & { type: 'plan_created' }
): PlanStep[] {
  return event.plan.steps.map((planStep, i) => ({
    id: planStep.id,
    type: 'plan' as const,
    status: 'pending' as const,
    label: planStep.title,
    // Queue not-yet-started steps at the bottom (preserve listed order via +i); each is restamped
    // to its own start-event seq in `plan_step_started` so it interleaves inline once it runs.
    order: PENDING_PLAN_STEP_ORDER_BASE + i,
    startedAt: event.ts,
  }));
}

/**
 * Create a running step from an `agent_step_started` event.
 * The step type is mapped from the backend stepType string.
 */
export function createAgentStep(
  event: ServerEvent & { type: 'agent_step_started' }
): GenericProgressStep | ContextRetrievalStep {
  const baseId = event.stepId;
  if (event.stepType === 'retrieve_context') {
    return {
      id: baseId,
      type: 'retrieve_context',
      status: 'running',
      label: event.label,
      order: event.seq,
      startedAt: event.ts,
    } satisfies ContextRetrievalStep;
  }
  return {
    id: baseId,
    type: 'generic_progress',
    status: 'running',
    label: event.label,
    order: event.seq,
    startedAt: event.ts,
  } satisfies GenericProgressStep;
}

/**
 * Transition a named step to `completed` from an `agent_step_completed` event.
 */
export function completeAgentStep(
  step: ExecutionStep,
  event: ServerEvent & { type: 'agent_step_completed' }
): ExecutionStep {
  return {
    ...step,
    status: 'completed' as const,
    label: event.label ?? step.label,
    detail: event.detail ?? (step as any).detail,
    ...(event.gaps !== undefined && { gaps: event.gaps }),
    completedAt: event.ts,
  };
}

/**
 * Transition a named step to `failed` from an `agent_step_failed` event.
 */
export function failAgentStep(
  step: ExecutionStep,
  event: ServerEvent & { type: 'agent_step_failed' }
): ExecutionStep {
  return {
    ...step,
    status: 'failed' as const,
    error: event.error,
    completedAt: event.ts,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Step updates
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update a tool execution step with progress delta.
 */
export function updateToolProgress(
  step: ToolExecutionStep,
  event: ServerEvent & { type: 'tool_delta' }
): ToolExecutionStep {
  return {
    ...step,
    progressText: (step.progressText ?? '') + event.delta,
  };
}

/**
 * Complete a tool execution step.
 */
export function completeToolStep(
  step: ToolExecutionStep,
  event: ServerEvent & { type: 'tool_completed' },
  completedAt: string
): ToolExecutionStep {
  const durationMs = step.startedAt
    ? new Date(completedAt).getTime() - new Date(step.startedAt).getTime()
    : undefined;

  return {
    ...step,
    status: event.success ? 'completed' : 'failed',
    resultSummary: event.resultSummary,
    completedAt,
    durationMs,
  };
}

/**
 * Complete a response generation step.
 */
export function completeResponseStep(
  step: ResponseGenerationStep,
  completedAt: string
): ResponseGenerationStep {
  return {
    ...step,
    status: 'completed',
    completedAt,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ExecutionStatus updates
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a new step to execution status.
 */
export function addStep(status: ExecutionStatus, step: ExecutionStep): ExecutionStatus {
  const newSteps = [...status.steps, step];
  return {
    ...status,
    steps: newSteps,
    activeStepId: step.status === 'running' ? step.id : status.activeStepId,
    phase: deriveExecutionPhase(
      status.phase === 'completed' ? 'streaming' : status.phase,
      newSteps
    ),
  };
}

/**
 * Update an existing step by ID.
 */
export function updateStep(
  status: ExecutionStatus,
  stepId: string,
  updater: (step: ExecutionStep) => ExecutionStep
): ExecutionStatus {
  const newSteps = status.steps.map((s) => (s.id === stepId ? updater(s) : s));
  const updatedStep = newSteps.find((s) => s.id === stepId);

  return {
    ...status,
    steps: newSteps,
    activeStepId:
      updatedStep && updatedStep.status === 'running'
        ? stepId
        : status.activeStepId === stepId
          ? undefined
          : status.activeStepId,
    phase: deriveExecutionPhase(
      status.phase === 'completed' ? 'streaming' : status.phase,
      newSteps
    ),
  };
}

/**
 * Mark execution as completed.
 */
export function completeExecution(status: ExecutionStatus, completedAt: string): ExecutionStatus {
  const durationMs = status.startedAt
    ? new Date(completedAt).getTime() - new Date(status.startedAt).getTime()
    : undefined;

  // Mark any running or pending steps as completed - pending steps were either
  // executed without emitting events (e.g. WS delivery gap) or were skipped
  // implicitly. A successful run completion means all planned work is done.
  const finalSteps = status.steps.map((step) =>
    step.status === 'running' || step.status === 'pending'
      ? { ...step, status: 'completed' as const, completedAt }
      : step
  );

  return {
    ...status,
    phase: 'completed',
    steps: finalSteps,
    activeStepId: undefined,
    completedAt,
    durationMs,
  };
}

/**
 * Mark execution as failed.
 */
export function failExecution(
  status: ExecutionStatus,
  error: string,
  completedAt: string
): ExecutionStatus {
  const durationMs = status.startedAt
    ? new Date(completedAt).getTime() - new Date(status.startedAt).getTime()
    : undefined;

  // Mark any running steps as failed
  const finalSteps = status.steps.map((step) =>
    step.status === 'running' ? { ...step, status: 'failed' as const, error, completedAt } : step
  );

  return {
    ...status,
    phase: 'failed',
    steps: finalSteps,
    activeStepId: undefined,
    error,
    completedAt,
    durationMs,
  };
}

/**
 * Mark execution as cancelled.
 */
export function cancelExecution(status: ExecutionStatus, completedAt: string): ExecutionStatus {
  const durationMs = status.startedAt
    ? new Date(completedAt).getTime() - new Date(status.startedAt).getTime()
    : undefined;

  // Mark any running steps as skipped
  const finalSteps = status.steps.map((step) =>
    step.status === 'running' ? { ...step, status: 'skipped' as const, completedAt } : step
  );

  return {
    ...status,
    phase: 'cancelled',
    steps: finalSteps,
    activeStepId: undefined,
    completedAt,
    durationMs,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Backward compatibility: Derive steps from legacy childEntries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build ExecutionStatus from legacy childEntries for backward compatibility.
 *
 * This is used to populate executionStatus from existing runs that may not
 * have been built with the new model.
 */
export function deriveExecutionStatusFromChildEntries(
  childEntries: RunChildEntry[],
  lifecycleState: string,
  startedAt: string,
  endedAt?: string
): ExecutionStatus {
  const steps: ExecutionStep[] = childEntries.map((entry) => {
    if (entry.kind === 'status') {
      return {
        id: entry.id,
        type: 'generic_progress' as const,
        status: 'completed' as const,
        label: entry.label,
        startedAt: entry.ts,
        completedAt: entry.ts,
      };
    }
    // tool entry
    return {
      id: entry.toolCallId,
      type: 'tool_execution' as const,
      status: entry.status,
      label: entry.label ?? entry.toolName,
      toolCallId: entry.toolCallId,
      toolName: entry.toolName,
      progressText: entry.progressText,
      resultSummary: entry.resultSummary,
      startedAt: entry.ts,
      completedAt: entry.status !== 'running' ? entry.ts : undefined,
    };
  });

  const phase = deriveExecutionPhase(lifecycleState, steps);
  const durationMs =
    endedAt && startedAt ? new Date(endedAt).getTime() - new Date(startedAt).getTime() : undefined;

  return {
    phase,
    steps,
    activeStepId: steps.find((s) => s.status === 'running')?.id,
    startedAt,
    completedAt: endedAt,
    durationMs,
  };
}
